import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "inputRef", "value", "label", "labelProps", "isSearching", "withBorder", "transparent", "onSubmit", "onClear", "wrapperRef", "wrapperProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.search.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { pxToRem } from 'utils/ui/units';
import { alpha } from 'utils/ui/colors';
import { spacing } from 'styles/globals';
import { textInputReset } from 'components/TextInput';
import TooltipButton from 'components/TooltipButton';
import VisuallyHidden from 'components/VisuallyHidden';
import useUniqueId from 'hooks/useUniqueId';
import useMergedRefs from 'hooks/useMergedRefs';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var SEARCH_BOX_HEIGHT = pxToRem(40);
var SEARCH_BUTTON_HEIGHT = pxToRem(38);
var Wrapper = styled.div.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['withBorder', 'transparent', 'isFocused'].includes(prop);
  }
}).withConfig({
  displayName: "SearchBox__Wrapper",
  componentId: "sc-8k5vaw-0"
})(["display:flex;height:", ";margin:0;padding:0;padding-left:", ";align-items:center;background-color:", ";border:1px solid transparent;border-radius:", ";cursor:text;transition:all 0.25s ease-in;&:has(input:focus){border-color:", ";box-shadow:0 0 0 1px ", ";transition-timing-function:ease-out;}", " ", ""], SEARCH_BOX_HEIGHT, spacing.xs, function (p) {
  return alpha(p.theme.shade, p.theme.shadeStrength);
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.withBorder && css(["border-color:", ";"], function (p) {
    return alpha(p.theme.shade, p.theme.lineStrength);
  });
}, function (p) {
  return p.transparent && css(["background-color:transparent;border-color:transparent;"]);
});
var InputWrapper = styled.div.withConfig({
  displayName: "SearchBox__InputWrapper",
  componentId: "sc-8k5vaw-1"
})(["flex:1 1 auto;"]);
var Input = styled.input.withConfig({
  displayName: "SearchBox__Input",
  componentId: "sc-8k5vaw-2"
})(["", " width:100%;padding:0 ", ";color:", ";&::placeholder{color:", ";}&::-ms-clear,&::-ms-reveal{display:none;width:0;height:0;}"], textInputReset, spacing.xs, function (p) {
  return p.theme.text;
}, function (p) {
  return alpha(p.theme.text, p.theme.textDimStrength);
});
export var SearchShapedButton = styled(TooltipButton).withConfig({
  displayName: "SearchBox__SearchShapedButton",
  componentId: "sc-8k5vaw-3"
})(["", " border-radius:", ";"], function (p) {
  return (!p.size || p.size === 'default') && "\n\t\tmin-height: auto;\n\t\theight: ".concat(SEARCH_BUTTON_HEIGHT, ";\n\t\t");
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.search);
});
function SearchBox(_ref) {
  var id = _ref.id,
    inputRefProp = _ref.inputRef,
    value = _ref.value,
    label = _ref.label,
    labelProps = _ref.labelProps,
    isSearching = _ref.isSearching,
    withBorder = _ref.withBorder,
    transparent = _ref.transparent,
    onSubmit = _ref.onSubmit,
    onClear = _ref.onClear,
    wrapperRef = _ref.wrapperRef,
    wrapperProps = _ref.wrapperProps,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var uniqueId = useUniqueId(id);
  var inputRef = useRef(null);
  var inputRefs = useMergedRefs([inputRef, inputRefProp]);
  function focusInput(e) {
    if (e.currentTarget === e.target) {
      var _inputRef$current;
      e.preventDefault();
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
    }
  }
  function submitSearch(e) {
    if (onSubmit) {
      e.stopPropagation();
      e.preventDefault();
      onSubmit();
    }
  }
  var hasSearchText = !!value;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(VisuallyHidden, _objectSpread(_objectSpread({
      as: "label",
      htmlFor: uniqueId
    }, labelProps), {}, {
      children: label || t('actions_search')
    })), /*#__PURE__*/_jsxs(Wrapper, {
      ref: wrapperRef,
      as: onSubmit ? 'form' : 'div',
      onSubmit: submitSearch,
      transparent: transparent,
      withBorder: withBorder,
      onMouseDown: focusInput,
      children: [/*#__PURE__*/_jsx(InputWrapper, {
        children: /*#__PURE__*/_jsx(Input, _objectSpread({
          type: "text",
          id: uniqueId,
          ref: inputRefs,
          autoComplete: "off",
          value: value || ''
        }, otherProps))
      }), onClear && hasSearchText && /*#__PURE__*/_jsx(SearchShapedButton, {
        iconOnly: true,
        icon: "x",
        size: "medium",
        variant: "transparent",
        onClick: function onClick() {
          return onClear(inputRef);
        },
        mt: 1,
        children: t('actions_clear_search')
      }), /*#__PURE__*/_jsx(SearchShapedButton, {
        iconOnly: true,
        iconRight: "search",
        iconScale: 1.5,
        variant: hasSearchText ? 'primary' : 'transparent',
        isLoading: isSearching,
        type: "submit",
        ml: "xxxs",
        children: t('actions_search')
      })]
    })]
  });
}
export default SearchBox;